import {
  decodeElevation,
  latLngToLocalMeters,
  sampleElevationLatLng,
  type DecodedElevation,
} from '@roadworld/map-generator';
import type { ChunkPayload } from '@roadworld/shared';
import type { HeightfieldData, MeshGeometry } from './geometry.js';

export interface TerrainBuildOptions {
  /** Vertices per side, e.g. 128 for a 128x128 grid. */
  resolution: number;
}

export interface TerrainResult {
  mesh: MeshGeometry;
  heightfield: HeightfieldData;
}

/**
 * Builds a square terrain mesh centered on the chunk origin, covering sizeMeters x sizeMeters.
 * Vertices sample the elevation grid via lat/lng so the terrain follows real DEM data.
 */
export function buildTerrain(payload: ChunkPayload, options: TerrainBuildOptions): TerrainResult {
  const { resolution } = options;
  const elevation = decodeElevation(payload.elevation);
  const half = payload.sizeMeters * 0.5;
  const step = payload.sizeMeters / (resolution - 1);

  const vertexCount = resolution * resolution;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const heights = new Float32Array(vertexCount);

  // Real elevation in meters. Don't subtract a per-chunk min — neighboring chunks need
  // matching absolute Y at shared edges for streaming seams to line up.
  for (let row = 0; row < resolution; row++) {
    for (let col = 0; col < resolution; col++) {
      const i = row * resolution + col;
      const x = -half + col * step;
      const z = -half + row * step;
      const lat = payload.origin.lat + z / metersPerDegreeLat();
      const lng = payload.origin.lng + x / metersPerDegreeLng(payload.origin.lat);
      const h = sampleElevationOrZero(elevation, lat, lng);
      heights[i] = h;
      positions[i * 3 + 0] = x;
      positions[i * 3 + 1] = h;
      positions[i * 3 + 2] = z;
      uvs[i * 2 + 0] = col / (resolution - 1);
      uvs[i * 2 + 1] = row / (resolution - 1);
    }
  }

  // Indices: two triangles per cell, CCW when viewed from +Y.
  const cellCount = (resolution - 1) * (resolution - 1);
  const indices = new Uint32Array(cellCount * 6);
  let ptr = 0;
  for (let row = 0; row < resolution - 1; row++) {
    for (let col = 0; col < resolution - 1; col++) {
      const a = row * resolution + col;
      const b = row * resolution + (col + 1);
      const c = (row + 1) * resolution + col;
      const d = (row + 1) * resolution + (col + 1);
      indices[ptr++] = a;
      indices[ptr++] = c;
      indices[ptr++] = b;
      indices[ptr++] = b;
      indices[ptr++] = c;
      indices[ptr++] = d;
    }
  }

  computeNormals(positions, indices, normals);

  return {
    mesh: { positions, indices, normals, uvs },
    heightfield: {
      resolution,
      sizeMeters: payload.sizeMeters,
      heights,
    },
  };
}

function sampleElevationOrZero(grid: DecodedElevation, lat: number, lng: number): number {
  if (lat < grid.south || lat > grid.north || lng < grid.west || lng > grid.east) return 0;
  return sampleElevationLatLng(grid, lat, lng);
}

function computeNormals(positions: Float32Array, indices: Uint32Array, out: Float32Array): void {
  out.fill(0);
  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i]! * 3;
    const ib = indices[i + 1]! * 3;
    const ic = indices[i + 2]! * 3;

    const ax = positions[ia]!;
    const ay = positions[ia + 1]!;
    const az = positions[ia + 2]!;
    const bx = positions[ib]!;
    const by = positions[ib + 1]!;
    const bz = positions[ib + 2]!;
    const cx = positions[ic]!;
    const cy = positions[ic + 1]!;
    const cz = positions[ic + 2]!;

    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = cx - ax;
    const vy = cy - ay;
    const vz = cz - az;

    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;

    out[ia] = out[ia]! + nx;
    out[ia + 1] = out[ia + 1]! + ny;
    out[ia + 2] = out[ia + 2]! + nz;
    out[ib] = out[ib]! + nx;
    out[ib + 1] = out[ib + 1]! + ny;
    out[ib + 2] = out[ib + 2]! + nz;
    out[ic] = out[ic]! + nx;
    out[ic + 1] = out[ic + 1]! + ny;
    out[ic + 2] = out[ic + 2]! + nz;
  }
  for (let i = 0; i < out.length; i += 3) {
    const x = out[i]!;
    const y = out[i + 1]!;
    const z = out[i + 2]!;
    const len = Math.hypot(x, y, z) || 1;
    out[i] = x / len;
    out[i + 1] = y / len;
    out[i + 2] = z / len;
  }
}

// Local copies to avoid pulling in projection's full API.
const EARTH_RADIUS_M = 6_378_137;
function metersPerDegreeLat(): number {
  return (EARTH_RADIUS_M * Math.PI) / 180;
}
function metersPerDegreeLng(lat: number): number {
  return (EARTH_RADIUS_M * Math.cos((lat * Math.PI) / 180) * Math.PI) / 180;
}

// Re-export the projection so the client doesn't depend on map-generator directly when it
// just needs to convert world meters into the same frame as the terrain.
export { latLngToLocalMeters };
